import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Messaging, getMessaging, getToken, onMessage } from 'firebase/messaging';
import { initializeApp } from 'firebase/app';
import { NzMessageService } from 'ng-zorro-antd/message';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { IUser } from '../interfaces/user.interface';
import { Router } from '@angular/router';
import { FIREBASE_CONFIG } from '../constants/app.constant';
import { MediaUploadService } from './media-upload.service';
import { CookieStorageService } from '../helpers/cookie-storage.service';
import { detectIncognito } from 'detectincognitojs';
import { ToastMessageService } from './toast-message.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationMessageService {
    private readonly currentToken = new BehaviorSubject<string | null>(null);
    private readonly messaging!: Messaging;
    private readonly nzMessageService = inject(NzMessageService);
    private readonly toastMessageService = inject(ToastMessageService);
    private readonly httpClient = inject(HttpClient);
    private readonly router = inject(Router);

    private readonly firebaseConfig = FIREBASE_CONFIG;

    private readonly vapidKey = environment.vapidKey;
    userDetail!: IUser;
    private readonly cookieService = inject(CookieStorageService);
    private readonly mediaUploadFileService = inject(MediaUploadService);

    constructor() {
        try {
            const app = initializeApp(this.firebaseConfig);
            this.messaging = getMessaging(app);
            this.setupForegroundMessageHandler();
        } catch (error: any) {
            this.toastMessageService.error(error);
        }
    }

    private setupForegroundMessageHandler(): void {
        onMessage(this.messaging, () => {
            this.showNotification();
        });
    }

    private showNotification(): void {
        this.userDetail = this.cookieService.get('userInfo') as IUser;
        this.toastMessageService.info(`Hii ${this.userDetail?.firstName} ${this.userDetail?.lastName} you have received one notification`, {
            nzDuration: 5000
        });
        this.cookieService.set('hasUnreadNotifications', true);
        this.mediaUploadFileService.setNotificationCount(true);
        const messageElement = document.querySelector('.ant-message-notice-content');
        if (messageElement) {
            messageElement.addEventListener('click', () => {
                this.router.navigateByUrl('/notifications');
            });
        }
    }

    async requestPermission(): Promise<string | null> {
        try {
            const result = await detectIncognito();

            if (result.isPrivate) {
                this.nzMessageService.warning('Notifications are not supported in private browsing mode');
                return null;
            }

            if (!('Notification' in window)) {
                throw new Error('This browser does not support notifications');
            }

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                throw new Error('Notification permission denied');
            }

            const currentToken = await getToken(this.messaging, {
                vapidKey: this.vapidKey
            });

            if (currentToken) {
                this.currentToken.next(currentToken);
                const payload = {
                    deviceIdentifier: navigator.userAgent,
                    fcmToken: currentToken
                };
                this.httpClient.post(`${environment.baseUrl}/users/manageDevice`, payload).subscribe();
                return currentToken;
            }

            throw new Error('No registration token available');
        } catch (error) {
            //this.nzMessageService.error(`Failed to setup notifications ${error}`);
            console.log(`Failed to setup notifications ${error}`);
            return null;
        }
    }

    getTokenObservable() {
        return this.currentToken.asObservable();
    }
}
